/**
 * React Query hooks for Google Workspace integration
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { IntegrationTileQueryOptions } from '../../api/integrationTileQuery';
import { googleIntegrationApi, GoogleIntegrationStatus } from '../services/googleIntegrationApi';

const INTEGRATION_QUERY_KEY = 'google-integration';

/**
 * Fetch Google integration status (OAuth scopes and connection state).
 *
 * Most call sites (Sidebar, file pickers) only need the status once per mount.
 * A connection tile that must stay current passes
 * `INTEGRATION_TILE_QUERY_OPTIONS`; React Query applies refetch options per
 * observer, so that stays scoped to the tile even though the key is shared.
 */
export function useGoogleIntegration(options?: IntegrationTileQueryOptions) {
  return useQuery<{ data: GoogleIntegrationStatus }>({
    queryKey: [INTEGRATION_QUERY_KEY],
    queryFn: () => googleIntegrationApi.getStatus(),
    ...options,
  });
}

export function useInitiateGoogleAuth() {
  return useMutation({
    mutationFn: (scopes?: string[]) => googleIntegrationApi.initiateAuth(scopes),
  });
}

export function useDisconnectGoogle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => googleIntegrationApi.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INTEGRATION_QUERY_KEY] });
    },
  });
}
